import { Application, Container, Graphics, Rectangle, Text, TilingSprite } from 'pixi.js';
import { useEffect, useRef } from 'react';
import {
  DECOR,
  FIELD_ICONS,
  FIELD_STYLE,
  HEX_SIZE,
  TERRAIN_PALETTE,
  hexCorners,
  hexHash,
  hexToPixel,
  pixelToHex,
} from '../lib/hex.js';

export interface MapHive {
  id: number;
  q: number;
  r: number;
  name: string;
  isMine: boolean;
  protectionUntil: Date | null;
  queenlessSince?: Date | null;
  ownerId?: number | null;
  relation?: 'ALLIED' | 'NEUTRAL' | 'HOSTILE';
}
export interface MapField {
  id: number;
  q: number;
  r: number;
  type: string;
  parties: number;
  mine: boolean;
  resource?: 'nectar' | 'pollen';
}
export interface MapMovement {
  id: number;
  status: string;
  from: { q: number; r: number };
  to: { q: number; r: number };
  departedAt: Date;
  arrivesAt: Date;
  leavesAt: Date | null;
  returnsAt: Date | null;
  /** eingehend (auf meinen Stock) – rot gezeichnet */
  incoming?: boolean;
}
export type MapHighlight = 'free' | 'queenless' | 'hostile' | 'nectar' | 'pollen' | null;
export interface MapViewport {
  center: { q: number; r: number };
  zoom: number;
  /** sichtbarer Bereich in Weltpixeln */
  rect: { x0: number; y0: number; x1: number; y1: number };
}
export interface HexMapApi {
  zoomBy: (factor: number) => void;
  centerOn: (q: number, r: number) => void;
  panBy: (dx: number, dy: number) => void;
}
export interface HexMapProps {
  hives: MapHive[];
  fields: MapField[];
  movements: MapMovement[];
  center: { q: number; r: number };
  selected: { q: number; r: number } | null;
  highlight?: MapHighlight;
  onSelect: (hex: { q: number; r: number } | null) => void;
  /** Wird aufgerufen, wenn die Kamera weit genug gewandert ist, um neue Daten zu brauchen. */
  onCenterChange: (hex: { q: number; r: number }) => void;
  onViewport?: (v: MapViewport) => void;
  onReady?: (api: HexMapApi) => void;
  className?: string;
}

const MIN_ZOOM = 0.3;
const MAX_ZOOM = 2.8;
const BG = 0x151d10;
const SQRT3 = Math.sqrt(3);
/** Kachel des Untergrunds: 6 Spalten x 6 Reihen Hexe, wiederholt sich periodisch. */
const TILE_COLS = 6;
const TILE_ROWS = 6;
const HEX_W = SQRT3 * HEX_SIZE;
const TILE_W = TILE_COLS * HEX_W;
const TILE_H = TILE_ROWS * 1.5 * HEX_SIZE;
/** Zellen-Rand um den Sichtbereich, damit kleine Schwenks kein Neuzeichnen ausloesen. */
const RANGE_MARGIN = 3;

type Lod = 'far' | 'mid' | 'near';
const lodFor = (zoom: number): Lod => (zoom < 0.5 ? 'far' : zoom < 0.95 ? 'mid' : 'near');

interface Range {
  qMin: number;
  qMax: number;
  rMin: number;
  rMax: number;
}
const contains = (outer: Range, inner: Range) =>
  inner.qMin >= outer.qMin &&
  inner.qMax <= outer.qMax &&
  inner.rMin >= outer.rMin &&
  inner.rMax <= outer.rMax;

/**
 * PixiJS-Hexkarte. Untergrund als gekachelte Textur (einmal gerendert), Felder und Stoecke als
 * Vektoren nur fuer den sichtbaren Bereich plus Rand, neu gezeichnet erst wenn der Bereich, die
 * Detailstufe oder die Daten wechseln. Pan per Drag/Touch, Zoom per Rad/Pinch/Buttons, Tippen waehlt.
 */
export function HexMap(props: HexMapProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const propsRef = useRef(props);
  propsRef.current = props;
  const apiRef = useRef<{ invalidate: () => void } | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let destroyed = false;
    const app = new Application();
    const world = new Container();
    const terrain = new Container();
    const entityG = new Graphics();
    const decor = new Container();
    const labels = new Container();
    const moveG = new Graphics();
    const bees = new Container();
    world.addChild(terrain, entityG, decor, labels, moveG, bees);
    let terrainSprite: TilingSprite | null = null;
    let drawnRange: Range | null = null;
    let drawnLod: Lod | null = null;
    let dataVersion = 0;
    let drawnVersion = -1;
    let viewDirty = true;
    let lastReported = { ...propsRef.current.center };
    let lastViewportEmit = 0;

    const screenToWorld = (sx: number, sy: number) => ({
      x: (sx - world.position.x) / world.scale.x,
      y: (sy - world.position.y) / world.scale.y,
    });

    const centerOn = (q: number, r: number) => {
      const p = hexToPixel(q, r);
      world.position.set(
        app.screen.width / 2 - p.x * world.scale.x,
        app.screen.height / 2 - p.y * world.scale.y,
      );
      viewDirty = true;
    };

    const visibleBounds = () => {
      const tl = screenToWorld(0, 0);
      const br = screenToWorld(app.screen.width, app.screen.height);
      const m = HEX_SIZE * 2;
      return { x0: tl.x - m, y0: tl.y - m, x1: br.x + m, y1: br.y + m };
    };

    const rangeFor = (b: { x0: number; y0: number; x1: number; y1: number }, margin: number) => {
      const corners = [
        pixelToHex(b.x0, b.y0),
        pixelToHex(b.x1, b.y0),
        pixelToHex(b.x0, b.y1),
        pixelToHex(b.x1, b.y1),
      ];
      return {
        qMin: Math.min(...corners.map((c) => c.q)) - margin,
        qMax: Math.max(...corners.map((c) => c.q)) + margin,
        rMin: Math.min(...corners.map((c) => c.r)) - margin,
        rMax: Math.max(...corners.map((c) => c.r)) + margin,
      };
    };

    // ---- Untergrund: eine Kachel mit 6x6 Hexen, deterministisch variiert, periodisch
    const buildTerrainTexture = () => {
      const g = new Graphics();
      for (let j = -1; j <= TILE_ROWS; j++) {
        for (let i = -1; i <= TILE_COLS + 1; i++) {
          const cx = HEX_W * i + (((j % 2) + 2) % 2) * (HEX_W / 2);
          const cy = 1.5 * HEX_SIZE * j;
          const jj = ((j % TILE_ROWS) + TILE_ROWS) % TILE_ROWS;
          const ii = ((i % TILE_COLS) + TILE_COLS) % TILE_COLS;
          const color = TERRAIN_PALETTE[hexHash(ii * 7 + 1, jj * 11 + 3) % TERRAIN_PALETTE.length]!;
          g.poly(hexCorners(cx, cy, HEX_SIZE, 0.985)).fill({ color });
        }
      }
      const tex = app.renderer.generateTexture({
        target: g,
        resolution: 2,
        frame: new Rectangle(0, 0, TILE_W, TILE_H),
      });
      g.destroy();
      return tex;
    };

    /** Nur Position/Groesse des Kachel-Sprites nachfuehren – billig, bei jedem Schwenk. */
    const updateTerrain = () => {
      if (!terrainSprite) return;
      const b = visibleBounds();
      // Ausrichtung auf Kachelperiode; Reihe r hat den Versatz (r mod 2) * HEX_W/2, was die Kachel abbildet.
      const x0 = Math.floor(b.x0 / TILE_W) * TILE_W;
      const y0 = Math.floor(b.y0 / TILE_H) * TILE_H;
      terrainSprite.position.set(x0, y0);
      terrainSprite.width = Math.ceil((b.x1 - x0) / TILE_W) * TILE_W;
      terrainSprite.height = Math.ceil((b.y1 - y0) / TILE_H) * TILE_H;
    };

    const labelCache = new Map<string, Text>();
    const decorCache = new Map<string, Text>();
    const getText = (
      cache: Map<string, Text>,
      parent: Container,
      key: string,
      text: string,
      fontSize: number,
    ) => {
      let t = cache.get(key);
      if (!t) {
        t = new Text({
          text,
          style: {
            fontSize,
            fill: 0xffffff,
            fontFamily: 'system-ui, "Segoe UI Emoji", sans-serif',
          },
        });
        t.anchor.set(0.5);
        t.resolution = 2;
        cache.set(key, t);
        parent.addChild(t);
      } else if (t.text !== text) t.text = text;
      t.visible = true;
      return t;
    };

    const matchesHighlight = (
      hl: MapHighlight,
      h: MapHive | undefined,
      f: MapField | undefined,
    ) => {
      if (!hl) return true;
      if (h) {
        if (hl === 'free') return h.ownerId === null;
        if (hl === 'queenless') return !!h.queenlessSince && h.ownerId !== null;
        if (hl === 'hostile') return h.relation === 'HOSTILE';
        return false;
      }
      if (f) {
        if (hl === 'nectar') return f.resource === 'nectar';
        if (hl === 'pollen') return f.resource === 'pollen';
        return false;
      }
      return false;
    };

    /** Felder und Stoecke im Bereich zeichnen (iteriert die Daten, nicht jede Zelle). */
    const drawEntities = (range: Range, lod: Lod) => {
      const { hives, fields, selected, highlight = null } = propsRef.current;
      entityG.clear();
      const usedLabels = new Set<string>();
      const usedDecor = new Set<string>();
      const inRange = (q: number, r: number) =>
        q >= range.qMin && q <= range.qMax && r >= range.rMin && r <= range.rMax;
      const dim = (match: boolean) => (highlight && !match ? 0.35 : 1);

      for (const f of fields) {
        if (!inRange(f.q, f.r)) continue;
        const p = hexToPixel(f.q, f.r);
        const key = `${f.q},${f.r}`;
        const hash = hexHash(f.q, f.r);
        const st = FIELD_STYLE[f.type] ?? { fill: 0x666666, rim: 0x999999, blossom: 0xffffff };
        const match = matchesHighlight(highlight, undefined, f);
        const a = dim(match);
        if (lod === 'far') {
          entityG.poly(hexCorners(p.x, p.y, HEX_SIZE, 0.8)).fill({ color: st.fill, alpha: a });
        } else {
          entityG.poly(hexCorners(p.x, p.y, HEX_SIZE, 0.93)).fill({ color: st.fill, alpha: a });
          entityG
            .poly(hexCorners(p.x, p.y, HEX_SIZE, 0.93))
            .stroke({ color: st.rim, width: f.mine ? 3 : 1.5, alpha: (f.mine ? 1 : 0.7) * a });
          for (let i = 0; i < 5; i++) {
            const ang = ((hash >>> (i * 3)) % 360) * (Math.PI / 180);
            const d = 6 + ((hash >>> (i * 2 + 7)) % 12);
            entityG
              .circle(p.x + Math.cos(ang) * d, p.y + Math.sin(ang) * d, 2.2)
              .fill({ color: st.blossom, alpha: 0.85 * a });
          }
          if (f.parties > 0)
            for (let i = 0; i < Math.min(6, f.parties); i++)
              entityG
                .circle(p.x - 10 + i * 4, p.y - HEX_SIZE * 0.62, 1.8)
                .fill({ color: f.mine && i === 0 ? 0xfbbf24 : 0xffffff, alpha: a });
          if (lod === 'near') {
            usedLabels.add(key);
            const t = getText(labelCache, labels, key, FIELD_ICONS[f.type] ?? '🌼', 16);
            t.position.set(p.x, p.y + 1);
            t.alpha = a;
          }
        }
        if (highlight && match)
          entityG.poly(hexCorners(p.x, p.y, HEX_SIZE, 1.0)).stroke({ color: 0xfacc15, width: 2.5 });
      }

      for (const h of hives) {
        if (!inRange(h.q, h.r)) continue;
        const p = hexToPixel(h.q, h.r);
        const key = `${h.q},${h.r}`;
        const empty = h.ownerId === null;
        const match = matchesHighlight(highlight, h, undefined);
        const a = dim(match);
        const body = empty
          ? 0x292524
          : h.isMine
            ? 0xb45309
            : h.relation === 'ALLIED'
              ? 0x166534
              : h.relation === 'HOSTILE'
                ? 0x7f1d1d
                : 0x57534e;
        const core = empty
          ? 0x57534e
          : h.isMine
            ? 0xf59e0b
            : h.relation === 'ALLIED'
              ? 0x4ade80
              : h.relation === 'HOSTILE'
                ? 0xf87171
                : 0x8a8580;
        if (lod === 'far') {
          entityG.poly(hexCorners(p.x, p.y, HEX_SIZE, 0.85)).fill({ color: body, alpha: a });
          entityG.circle(p.x, p.y, HEX_SIZE * 0.35).fill({ color: core, alpha: a });
        } else {
          entityG.poly(hexCorners(p.x, p.y, HEX_SIZE, 0.93)).fill({ color: body, alpha: a });
          entityG.poly(hexCorners(p.x, p.y, HEX_SIZE, 0.62)).fill({ color: core, alpha: a });
          entityG.poly(hexCorners(p.x, p.y, HEX_SIZE, 0.34)).fill({ color: body, alpha: 0.7 * a });
          if (empty) {
            entityG
              .poly(hexCorners(p.x, p.y, HEX_SIZE, 0.93))
              .stroke({ color: 0x78716c, width: 1.5, alpha: 0.8 * a });
            if (lod === 'near') {
              usedLabels.add(key);
              const t = getText(labelCache, labels, key, '🏚', 13);
              t.position.set(p.x, p.y);
              t.alpha = 0.9 * a;
            }
          }
        }
        if (h.protectionUntil && h.protectionUntil.getTime() > Date.now())
          entityG
            .poly(hexCorners(p.x, p.y, HEX_SIZE, 0.93))
            .stroke({ color: 0x34d399, width: 2.5, alpha: a });
        else if (h.isMine)
          entityG
            .poly(hexCorners(p.x, p.y, HEX_SIZE, 0.93))
            .stroke({ color: 0xfde68a, width: 2, alpha: a });
        if (h.queenlessSince && !empty)
          entityG
            .poly(hexCorners(p.x, p.y, HEX_SIZE, 0.78))
            .stroke({ color: 0xc084fc, width: 2, alpha: 0.9 * a });
        if (lod === 'near' && !empty) {
          usedLabels.add(key);
          const t = getText(labelCache, labels, key, h.name, 10);
          t.position.set(p.x, p.y + HEX_SIZE * 0.98);
          t.alpha = a;
          const w = t.width + 8;
          entityG
            .roundRect(p.x - w / 2, p.y + HEX_SIZE * 0.98 - 8, w, 16, 6)
            .fill({ color: 0x0c0a09, alpha: 0.75 * a });
        }
        if (highlight && match)
          entityG
            .poly(hexCorners(p.x, p.y, HEX_SIZE, 1.02))
            .stroke({ color: 0xfacc15, width: 2.5 });
      }

      // Dekoration nur nah und nur auf leeren Zellen (sparsam)
      if (lod === 'near') {
        const occupied = new Set<string>([
          ...fields.map((f) => `${f.q},${f.r}`),
          ...hives.map((h) => `${h.q},${h.r}`),
        ]);
        for (let r = range.rMin; r <= range.rMax; r++)
          for (let q = range.qMin; q <= range.qMax; q++) {
            const hash = hexHash(q, r);
            if (hash % 17 !== 0) continue;
            const key = `${q},${r}`;
            if (occupied.has(key)) continue;
            const p = hexToPixel(q, r);
            usedDecor.add(key);
            const t = getText(decorCache, decor, key, DECOR[(hash >>> 5) % DECOR.length]!, 13);
            t.alpha = 0.8;
            t.position.set(p.x + ((hash >>> 9) % 7) - 3, p.y + ((hash >>> 12) % 7) - 3);
          }
      }

      if (selected) {
        const sp = hexToPixel(selected.q, selected.r);
        entityG.poly(hexCorners(sp.x, sp.y, HEX_SIZE, 1.02)).stroke({ color: 0xffffff, width: 3 });
        entityG
          .poly(hexCorners(sp.x, sp.y, HEX_SIZE, 1.12))
          .stroke({ color: 0xffffff, width: 1, alpha: 0.35 });
      }
      for (const [key, t] of labelCache) if (!usedLabels.has(key)) t.visible = false;
      for (const [key, t] of decorCache) if (!usedDecor.has(key)) t.visible = false;
    };

    /** Pro Frame: Untergrund nachfuehren, Entitaeten nur bei Bedarf neu zeichnen. */
    const updateView = () => {
      updateTerrain();
      const b = visibleBounds();
      const needed = rangeFor(b, 0);
      const lod = lodFor(world.scale.x);
      if (
        drawnRange &&
        drawnLod === lod &&
        drawnVersion === dataVersion &&
        contains(drawnRange, needed)
      )
        return;
      const range = rangeFor(b, RANGE_MARGIN);
      drawEntities(range, lod);
      drawnRange = range;
      drawnLod = lod;
      drawnVersion = dataVersion;
    };

    const emitViewport = (force = false) => {
      const now = performance.now();
      if (!force && now - lastViewportEmit < 150) return;
      lastViewportEmit = now;
      const c = screenToWorld(app.screen.width / 2, app.screen.height / 2);
      const tl = screenToWorld(0, 0);
      const br = screenToWorld(app.screen.width, app.screen.height);
      propsRef.current.onViewport?.({
        center: pixelToHex(c.x, c.y),
        zoom: world.scale.x,
        rect: { x0: tl.x, y0: tl.y, x1: br.x, y1: br.y },
      });
    };

    const beeCache = new Map<number, Text>();
    const drawMovements = () => {
      moveG.clear();
      const now = Date.now();
      const used = new Set<number>();
      for (const m of propsRef.current.movements) {
        const a = hexToPixel(m.from.q, m.from.r);
        const bb = hexToPixel(m.to.q, m.to.r);
        const color = m.incoming ? 0xf87171 : 0xfbbf24;
        const dx = bb.x - a.x;
        const dy = bb.y - a.y;
        const len = Math.hypot(dx, dy);
        const n = Math.max(1, Math.floor(len / 10));
        for (let i = 0; i < n; i += 2) {
          const t0 = i / n;
          const t1 = Math.min(1, (i + 1) / n);
          moveG
            .moveTo(a.x + dx * t0, a.y + dy * t0)
            .lineTo(a.x + dx * t1, a.y + dy * t1)
            .stroke({ color, width: m.incoming ? 2 : 1.5, alpha: m.incoming ? 0.7 : 0.45 });
        }
        let t = 0;
        let outbound = true;
        if (m.status === 'FLYING') {
          const total = m.arrivesAt.getTime() - m.departedAt.getTime();
          t = total > 0 ? Math.min(1, Math.max(0, (now - m.departedAt.getTime()) / total)) : 1;
        } else if (m.status === 'GATHERING' || m.status === 'STATIONED') {
          t = 1;
        } else if (m.status === 'RETURNING' && m.returnsAt) {
          outbound = false;
          const start = m.leavesAt
            ? Math.min(m.leavesAt.getTime(), m.returnsAt.getTime())
            : m.arrivesAt.getTime();
          const total = m.returnsAt.getTime() - start;
          t = total > 0 ? Math.min(1, Math.max(0, (now - start) / total)) : 1;
        }
        const from = outbound ? a : bb;
        const to = outbound ? bb : a;
        const x = from.x + (to.x - from.x) * t;
        const y = from.y + (to.y - from.y) * t + Math.sin(now / 180 + m.id) * 2;
        used.add(m.id);
        let bee = beeCache.get(m.id);
        if (!bee) {
          bee = new Text({ text: m.incoming ? '⚔' : '🐝', style: { fontSize: 15 } });
          bee.anchor.set(0.5);
          bee.resolution = 2;
          beeCache.set(m.id, bee);
          bees.addChild(bee);
        }
        bee.visible = true;
        bee.position.set(x, y);
        bee.scale.x = to.x < from.x ? -1 : 1;
        if (m.status === 'GATHERING' || m.status === 'STATIONED') {
          moveG
            .circle(bb.x, bb.y, HEX_SIZE * 0.5)
            .stroke({ color, width: 2, alpha: 0.5 + 0.3 * Math.sin(now / 400) });
        }
        if (m.incoming && m.status === 'FLYING') {
          moveG
            .circle(bb.x, bb.y, HEX_SIZE * (0.9 + 0.15 * Math.sin(now / 250)))
            .stroke({ color, width: 2.5, alpha: 0.8 });
        }
      }
      for (const [id, bee] of beeCache) if (!used.has(id)) bee.visible = false;
    };

    // ---- Eingabe: Pan, Pinch, Zoom, Tippen
    const pointers = new Map<number, { x: number; y: number }>();
    let dragStart: { x: number; y: number; wx: number; wy: number } | null = null;
    let moved = false;
    let pinchDist = 0;

    const reportCenter = () => {
      const c = screenToWorld(app.screen.width / 2, app.screen.height / 2);
      const h = pixelToHex(c.x, c.y);
      const d =
        (Math.abs(h.q - lastReported.q) +
          Math.abs(h.q + h.r - lastReported.q - lastReported.r) +
          Math.abs(h.r - lastReported.r)) /
        2;
      if (d >= 8) {
        lastReported = h;
        propsRef.current.onCenterChange(h);
      }
      emitViewport(true);
    };

    const zoomAt = (sx: number, sy: number, factor: number) => {
      const before = screenToWorld(sx, sy);
      const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, world.scale.x * factor));
      world.scale.set(next);
      world.position.set(sx - before.x * next, sy - before.y * next);
      viewDirty = true;
    };

    const onDown = (e: PointerEvent) => {
      (e.target as Element).setPointerCapture?.(e.pointerId);
      pointers.set(e.pointerId, { x: e.offsetX, y: e.offsetY });
      if (pointers.size === 1) {
        dragStart = { x: e.offsetX, y: e.offsetY, wx: world.position.x, wy: world.position.y };
        moved = false;
      } else if (pointers.size === 2) {
        const [p1, p2] = [...pointers.values()];
        pinchDist = Math.hypot(p1!.x - p2!.x, p1!.y - p2!.y);
        dragStart = null;
      }
    };
    const onMove = (e: PointerEvent) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.offsetX, y: e.offsetY });
      if (pointers.size === 2) {
        const [p1, p2] = [...pointers.values()];
        const d = Math.hypot(p1!.x - p2!.x, p1!.y - p2!.y);
        if (pinchDist > 0) zoomAt((p1!.x + p2!.x) / 2, (p1!.y + p2!.y) / 2, d / pinchDist);
        pinchDist = d;
        moved = true;
      } else if (dragStart) {
        const dx = e.offsetX - dragStart.x;
        const dy = e.offsetY - dragStart.y;
        if (Math.abs(dx) + Math.abs(dy) > 6) moved = true;
        world.position.set(dragStart.wx + dx, dragStart.wy + dy);
        viewDirty = true;
        emitViewport();
      }
    };
    const onUp = (e: PointerEvent) => {
      pointers.delete(e.pointerId);
      if (pointers.size === 0) {
        if (!moved && dragStart) {
          const w = screenToWorld(e.offsetX, e.offsetY);
          propsRef.current.onSelect(pixelToHex(w.x, w.y));
        }
        dragStart = null;
        reportCenter();
      }
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomAt(e.offsetX, e.offsetY, e.deltaY < 0 ? 1.15 : 1 / 1.15);
      reportCenter();
    };

    void app
      .init({
        resizeTo: host,
        background: BG,
        antialias: true,
        resolution: Math.min(2, window.devicePixelRatio || 1),
        autoDensity: true,
      })
      .then(() => {
        if (destroyed) {
          app.destroy(true);
          return;
        }
        host.appendChild(app.canvas);
        app.canvas.style.touchAction = 'none';
        app.stage.addChild(world);
        world.scale.set(1);
        terrainSprite = new TilingSprite({ texture: buildTerrainTexture(), width: 1, height: 1 });
        terrain.addChild(terrainSprite);
        centerOn(propsRef.current.center.q, propsRef.current.center.r);
        app.canvas.addEventListener('pointerdown', onDown);
        app.canvas.addEventListener('pointermove', onMove);
        app.canvas.addEventListener('pointerup', onUp);
        app.canvas.addEventListener('pointercancel', onUp);
        app.canvas.addEventListener('wheel', onWheel, { passive: false });
        app.renderer.on('resize', () => {
          viewDirty = true;
          emitViewport(true);
        });
        app.ticker.add(() => {
          if (viewDirty) {
            viewDirty = false;
            updateView();
          }
          drawMovements();
        });
        apiRef.current = {
          invalidate: () => {
            dataVersion++;
            viewDirty = true;
          },
        };
        propsRef.current.onReady?.({
          zoomBy: (f) => {
            zoomAt(app.screen.width / 2, app.screen.height / 2, f);
            reportCenter();
          },
          centerOn: (q, r) => {
            centerOn(q, r);
            reportCenter();
          },
          panBy: (dx, dy) => {
            world.position.set(world.position.x - dx, world.position.y - dy);
            viewDirty = true;
            reportCenter();
          },
        });
        emitViewport(true);
      });

    return () => {
      destroyed = true;
      apiRef.current = null;
      if (app.renderer) {
        app.canvas.removeEventListener('pointerdown', onDown);
        app.canvas.removeEventListener('pointermove', onMove);
        app.canvas.removeEventListener('pointerup', onUp);
        app.canvas.removeEventListener('pointercancel', onUp);
        app.canvas.removeEventListener('wheel', onWheel);
        app.destroy(true, { children: true });
      }
    };
  }, []);

  // Daten, Auswahl oder Filter geaendert -> Entitaeten neu zeichnen
  useEffect(() => {
    apiRef.current?.invalidate();
  }, [props.hives, props.fields, props.selected, props.highlight]);

  return (
    <div
      ref={hostRef}
      className={props.className ?? 'h-full w-full'}
      style={{ touchAction: 'none' }}
    />
  );
}
